<template>
  <div class="tw-relative tw-flex tw-items-center tw-gap-2">
    <TooltipPopup :position="tooltipPosition">
      <template #trigger>
        <span
          class="tw-inline-flex tw-h-fit tw-w-fit tw-items-center tw-space-x-1 tw-rounded-full tw-px-2 tw-py-1 tw-text-center tw-font-medium"
          :class="{
            'tw-bg-green-50 tw-text-green-600': statusConfig.colorVariant === 'green',
            'tw-bg-gray-50 tw-text-gray-600': statusConfig.colorVariant === 'gray',
            'tw-bg-red-50 tw-text-red-600': statusConfig.colorVariant === 'red',
            'tw-bg-yellow-50 tw-text-yellow-600': statusConfig.colorVariant === 'yellow'
          }"
        >
          <span class="tw-text-2xs">{{ statusConfig.title }}</span>
        </span>
      </template>
      <template v-if="statusConfig.tooltipText" #content>{{ statusConfig.tooltipText }}</template>
    </TooltipPopup>

    <!-- Warning Icon with Tooltip -->
    <div v-if="hasIssues">
      <TablerAlertTriangle
        data-testid="alert-triangle-icon"
        :class="{
          'tw-mb-0.5 tw-h-4 tw-w-4': true,
          'tw-text-amber-500': hasIssues === 'warning',
          'tw-text-red-500': hasIssues === 'error'
        }"
        @mouseenter="showTooltip = true"
        @mouseleave="showTooltip = false"
      />

      <Transition
        enterActiveClass="tw-transition tw-duration-200 tw-ease-out"
        enterFromClass="tw-transform tw-scale-95 tw-opacity-0"
        enterToClass="tw-transform tw-scale-100 tw-opacity-100"
        leaveActiveClass="tw-transition tw-duration-150 tw-ease-in"
        leaveFromClass="tw-transform tw-scale-100 tw-opacity-100"
        leaveToClass="tw-transform tw-scale-95 tw-opacity-0"
      >
        <div
          v-if="showTooltip"
          :class="{
            'tw-absolute tw-left-1/2 tw-z-50 tw-w-72 tw--translate-x-1/2 tw-rounded-md tw-bg-white tw-p-3 tw-shadow-lg': true,
            'tw-top-8': tooltipPosition === 'bottom',
            'tw-bottom-8': tooltipPosition === 'top'
          }"
          @mouseenter="showTooltip = true"
          @mouseleave="showTooltip = false"
        >
          <StudioAlertBanner v-if="videoAsset.errors.length > 0" type="error" class="tw-mb-2">
            <div v-for="error in videoAsset.errors" :key="error" class="tw-mb-1">
              {{ error }}
            </div>
          </StudioAlertBanner>
          <StudioAlertBanner v-if="videoAsset.warnings.length > 0" type="warning">
            <div v-for="warning in videoAsset.warnings" :key="warning" class="tw-mb-1">
              {{ warning }}
            </div>
          </StudioAlertBanner>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import { VideoAsset } from '../videoLibraryService';

import TablerAlertTriangle from '@/studioComponents/Icons/tabler/TablerAlertTriangle.vue';
import StudioAlertBanner from '@/studioComponents/StudioAlertBanner.vue';
import TooltipPopup from '@/studioComponents/TooltipPopup.vue';

const props = defineProps<{
  videoAsset: VideoAsset;
  tooltipPosition: 'top' | 'bottom';
}>();

const showTooltip = ref(false);

const hasIssues = computed(() => {
  if (!props.videoAsset.errors.length && !props.videoAsset.warnings.length) {
    return null;
  }

  return props.videoAsset.errors.length > 0 ? 'error' : 'warning';
});

const statusConfig = computed(() => {
  const statusMap = {
    SUBMITTED: {
      title: 'Pending',
      colorVariant: 'gray',
      tooltipText: ''
    },
    PROGRESSING: {
      title: 'Processing',
      colorVariant: 'gray',
      tooltipText: ''
    },
    PROCESSING_HD: {
      title: 'Processing HD',
      colorVariant: 'yellow',
      tooltipText: "We're optimizing the HD version in the background to ensure smoother playback in events."
    },
    COMPLETE: {
      title: 'Completed',
      colorVariant: 'green',
      tooltipText: ''
    },
    ERROR: {
      title: 'Error',
      colorVariant: 'red',
      tooltipText: ''
    },
    READY_TO_PUBLISH: {
      title: 'Ready to be Published',
      colorVariant: 'gray',
      tooltipText: ''
    }
  };

  return (
    statusMap[props.videoAsset.status] || {
      title: 'Unknown',
      colorVariant: 'gray',
      tooltipText: ''
    }
  );
});
</script>
